import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Button } from '@bravemove/ui-web';

/**
 * Route-level error boundary for the admin SPA.
 *
 * The admin app statically imports every page, and React 19 **unmounts the
 * entire tree** when a render throws with no boundary above it — so a single
 * page crash (e.g. an API client returning an unexpected shape → `rows.length`
 * on `undefined`) blanks the whole console and reads as "the page won't open".
 *
 * This boundary wraps the routed `<Outlet />` (inside `AdminShell`, so the top
 * nav + side nav stay usable), catches the throw, and renders a recovery card
 * instead of a white screen. It auto-clears when `resetKey` changes
 * (`AdminShell` passes the current pathname), so navigating to another page
 * recovers without a manual reload.
 *
 * The class can't call hooks, so the fallback UI is a separate function
 * component (`ErrorFallback`) which uses `useI18n()` for translated copy.
 */
interface RouteErrorBoundaryProps {
  /** Changing this value clears a caught error (pass the route pathname). */
  readonly resetKey?: string;
  readonly children: ReactNode;
}

interface RouteErrorBoundaryState {
  readonly error: Error | null;
}

export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  override state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface for diagnosis. A production build would forward this to
    // CloudWatch RUM / Sentry; for now the console + the visible card suffice.
    console.error('[admin] page render error:', error, info.componentStack);
  }

  override componentDidUpdate(prev: RouteErrorBoundaryProps): void {
    // Recover automatically once the user navigates to a different route.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return <ErrorFallback error={error} onReset={() => this.setState({ error: null })} />;
  }
}

function ErrorFallback({
  error,
  onReset,
}: {
  readonly error: Error;
  readonly onReset: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="flex min-h-[40vh] items-center justify-center p-6">
      <div className="max-w-lg w-full rounded-xl border border-hairline bg-surface p-6 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-danger">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
          </svg>
        </div>
        <h1 className="text-h3 font-semibold text-ink">{t('admin:errorBoundary.title')}</h1>
        <p className="mt-2 text-sm text-ink-2">{t('admin:errorBoundary.body')}</p>
        <pre className="mt-3 max-h-32 overflow-auto rounded-md bg-surface-2 p-3 text-start font-mono text-[11.5px] text-ink-3">
          {error.message}
        </pre>
        <div className="mt-5 flex items-center justify-center gap-3">
          <Button variant="secondary" onClick={onReset}>
            {t('admin:errorBoundary.tryAgain')}
          </Button>
          <Button variant="primary" onClick={() => window.location.assign('/')}>
            {t('admin:errorBoundary.backToDashboard')}
          </Button>
        </div>
      </div>
    </div>
  );
}
